import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import {
  datasetFilesService,
  DATASET_FILES_API_PATHS,
} from '../services/datasetFiles.service'
import type {
  DatasetFileListResponse,
  GetDatasetFileListRequest,
  ParseDatasetFileRequest,
  SublevelPreviewRequest,
  SublevelPreviewResponse,
  UploadDatasetFileRequest,
  SaveDatasetConfigRequest,
} from '../types/datasetFiles.type'

/**
 * 获取数据集文件列表 Hook
 * @param params 查询参数（数据集ID、搜索关键词、状态等）
 * @returns SWR 查询对象，包含 data、error、mutate 等
 */
export const useDatasetFileList = (params: GetDatasetFileListRequest) => {
  // 构建唯一的key，使用更稳定的方式
  const key = params.datasetId
    ? [
        DATASET_FILES_API_PATHS.LIST,
        params.datasetId,
        params.search || '',
        params.status || '',
        params.parseStatus || '',
      ]
    : null

  return useSWR<DatasetFileListResponse>(
    key,
    () => datasetFilesService.getDatasetFileList(params),
    {
      revalidateOnFocus: false, // 禁用焦点重新验证
      revalidateOnReconnect: false, // 禁用重连时重新验证
      revalidateIfStale: false, // 禁用过期数据重新验证
      dedupingInterval: 2000, // 增加去重间隔到2秒
      errorRetryCount: 1, // 减少重试次数
      errorRetryInterval: 1000, // 重试间隔
    }
  )
}

/**
 * 删除数据集文件 Hook
 * @returns SWR mutation 对象，包含 trigger、isMutating、error
 */
export const useDeleteDatasetFile = () => {
  return useSWRMutation<boolean, Error, string, string>(
    DATASET_FILES_API_PATHS.DELETE,
    (_, { arg }) => datasetFilesService.deleteDatasetFile(arg),
    {
      // 禁用重试
      revalidate: false,
      populateCache: false,
      throwOnError: false,
    }
  )
}

/**
 * 解析文件 Hook
 * @returns SWR mutation 对象，包含 trigger、isMutating、error
 */
export const useParseDatasetFile = () => {
  return useSWRMutation<boolean, Error, string, ParseDatasetFileRequest>(
    DATASET_FILES_API_PATHS.PARSE,
    (_, { arg }) => datasetFilesService.parseDatasetFile(arg)
  )
}

/**
 * 分段预览 Hook
 * @returns SWR mutation 对象，包含 trigger、isMutating、error
 */
export const useSublevelPreview = () => {
  return useSWRMutation<
    SublevelPreviewResponse,
    Error,
    string,
    SublevelPreviewRequest
  >(DATASET_FILES_API_PATHS.SUBLEVEL_PREVIEW, (_, { arg }) =>
    datasetFilesService.getSublevelPreview(arg)
  )
}

/**
 * 上传文件 Hook
 * @returns SWR mutation 对象，包含 trigger、isMutating、error
 */
export const useUploadDatasetFile = () => {
  return useSWRMutation<string[], Error, string, UploadDatasetFileRequest>(
    DATASET_FILES_API_PATHS.UPLOAD,
    (_, { arg }) => datasetFilesService.uploadDatasetFile(arg),
    {
      // 禁用重试
      revalidate: false,
      populateCache: false,
      throwOnError: false,
    }
  )
}

/**
 * 保存数据集配置 Hook
 * @returns SWR mutation 对象，包含 trigger、isMutating、error
 */
export const useSaveDatasetConfig = () => {
  return useSWRMutation<boolean, Error, string, SaveDatasetConfigRequest>(
    DATASET_FILES_API_PATHS.SAVE_CONFIG,
    (_, { arg }) => datasetFilesService.saveDatasetConfig(arg),
    {
      // 禁用重试
      revalidate: false,
      populateCache: false,
      throwOnError: false,
    }
  )
}

/**
 * 数据集文件操作组合 Hook
 * 提供完整的数据集文件管理功能，包括列表获取、删除、解析、预览、上传
 * 所有操作完成后会自动刷新列表
 * @param params 列表查询参数
 * @returns 包含所有数据集文件操作方法的对象
 */
export const useDatasetFiles = (params: GetDatasetFileListRequest) => {
  const { data, error, mutate } = useDatasetFileList(params)
  const { trigger: deleteFile, isMutating: isDeleting } = useDeleteDatasetFile()
  const { trigger: parseFile, isMutating: isParsing } = useParseDatasetFile()
  const { trigger: getPreview, isMutating: isGettingPreview } =
    useSublevelPreview()
  const { trigger: uploadFile, isMutating: isUploading } =
    useUploadDatasetFile()
  const { trigger: saveConfig, isMutating: isSaving } = useSaveDatasetConfig()

  /**
   * 删除数据集文件
   * @param id 文件ID
   * @returns Promise<boolean>
   */
  const removeFile = async (id: string) => {
    const result = await deleteFile(id)

    // 删除成功后手动更新列表，而不是触发重新请求
    mutate(
      (prev) => ({
        datasetFiles: (prev?.datasetFiles || []).filter(
          (file) => file.id !== id
        ),
        total: (prev?.total || 0) - 1,
      }),
      false // 不触发重新验证
    )
    return result
  }

  /**
   * 解析文件
   * @param parseParams 解析参数
   * @returns Promise<boolean>
   */
  const parseFiles = async (parseParams: ParseDatasetFileRequest) => {
    const result = await parseFile(parseParams)
    if (result) {
      await mutate() // 这里需要重新获取列表，因为解析状态改变了
    }
    return result
  }

  /**
   * 获取分段预览
   * @param previewParams 预览参数
   * @returns Promise<SublevelPreviewResponse>
   */
  const getFilePreview = async (previewParams: SublevelPreviewRequest) => {
    return await getPreview(previewParams)
  }

  /**
   * 上传文件
   * @param uploadParams 上传参数
   * @returns Promise<string[]> 返回文件ID列表
   */
  const uploadFiles = async (uploadParams: UploadDatasetFileRequest) => {
    const result = await uploadFile(uploadParams)
    if (result && result.length > 0) {
      await mutate() // 这里需要重新获取列表，因为新增了文件
    }
    return result
  }

  /**
   * 保存数据集配置
   * @param configParams 配置参数
   * @returns Promise<boolean>
   */
  const saveDatasetConfig = async (configParams: SaveDatasetConfigRequest) => {
    return await saveConfig(configParams)
  }

  return {
    // 列表数据
    fileList: data?.datasetFiles || [],
    total: data?.total || 0,
    isLoading: !data && !error,
    error,

    // 操作方法
    removeFile,
    parseFiles,
    getFilePreview,
    uploadFiles,
    saveDatasetConfig,

    // 加载状态
    isDeleting,
    isParsing,
    isGettingPreview,
    isUploading,
    isSaving,

    // 刷新方法
    refresh: mutate,
  }
}
